import React, { useState, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  Chip,
  Fade,
  Slide,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AddComment as AddCommentIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Transition for the dialog
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ShiftRemarksButton({ Employee_ID, onRemarksUpdated }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [shiftDetails, setShiftDetails] = useState(null);
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const [loadingShiftDetails, setLoadingShiftDetails] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const CHARACTER_LIMIT = 200;

  // Open/close
  const handleOpenDialog = useCallback(() => {
    setDialogOpen(true);
    setShiftDetails(null);
    setIsViewingDetails(false);
    setRemarks("");
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setRemarks("");
    setShiftDetails(null);
    setIsViewingDetails(false);
    setLoadingShiftDetails(false);
    setLoadingUpdate(false);
  }, []);

  // Snackbar helpers
  const showNotification = useCallback((message, severity = "success") => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Formatters
  const formatDateTime = useCallback((date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  }, []);

  const formatShiftLoggedIn = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(date);
    } catch {
      return "Invalid Date";
    }
  }, []);

  // Validation + helper text
  const isRemarksValid = useCallback(() => {
    const trimmed = remarks.trim();
    return trimmed.length > 0 && trimmed.length <= CHARACTER_LIMIT;
  }, [remarks]);

  const getRemarksHelperText = useCallback(() => {
    const length = remarks.length;
    const remaining = CHARACTER_LIMIT - length;
    if (length === 0) return "Enter your remarks here";
    if (remaining < 10) return `${remaining} characters remaining`;
    return `${length}/${CHARACTER_LIMIT}`;
  }, [remarks]);

  const getRemarksColor = useCallback(() => {
    const remaining = CHARACTER_LIMIT - remarks.length;
    if (remaining < 10) return "warning";
    if (remaining < 50) return "info";
    return "primary";
  }, [remarks]);

  // Submit remarks
  const handleUpdateRemarks = useCallback(async () => {
    if (!isRemarksValid()) return;

    setLoadingUpdate(true);
    try {
      const trimmedRemarks = remarks.replace(/\s{2,}/g, " ").trim();
      const currentDateTime = new Date();
      const formattedDateTime = formatDateTime(currentDateTime);
      const updatedRemarks = `${trimmedRemarks} - (${formattedDateTime})`;

      const response = await axios.post(`${API_BASE_URL}/updateShiftRemarks`, {
        Employee_ID,
        remark: updatedRemarks,
      });

      if (response.data.message === "Remarks updated successfully") {
        showNotification("Shift remarks updated successfully!", "success");
        handleCloseDialog();
        onRemarksUpdated?.(updatedRemarks);
      } else {
        throw new Error(response.data.error || "Unknown error occurred");
      }
    } catch (error) {
      console.error("Error updating remarks:", error);
      showNotification(
        error.response?.data?.message ||
          "Failed to update remarks. Please try again.",
        "error"
      );
    } finally {
      setLoadingUpdate(false);
    }
  }, [remarks, Employee_ID, showNotification, handleCloseDialog, onRemarksUpdated, formatDateTime, isRemarksValid]);

  // Fetch shift details
  const handleViewShiftDetails = useCallback(async () => {
    setLoadingShiftDetails(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/getShiftDetails`, {
        params: { Employee_ID },
      });
      setShiftDetails(response.data.data);
      setIsViewingDetails(true);
    } catch (error) {
      console.error("Error fetching shift details:", error);
      showNotification("Failed to load shift details. Please try again.", "error");
    } finally {
      setLoadingShiftDetails(false);
    }
  }, [Employee_ID, showNotification]);

  // Theme-aware helpers
  const headerGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`;
  const subtleGlass = alpha(theme.palette.background.paper, 0.95);
  const surfaceBorder = `1px solid ${alpha(theme.palette.divider, 0.5)}`;

  return (
    <>
      {/* Launcher Button (theme-aware gradient) */}
      <Tooltip title="Add or view shift remarks" arrow>
        <Button
          startIcon={<AddCommentIcon />}
          onClick={handleOpenDialog}
          size="small"
          variant="contained"
          disabled={!Employee_ID}
          sx={{
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            px: 2,
            py: 1,
            color: theme.palette.common.white,
            background: headerGradient,
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
            "&:disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
              border: "none",
            },
          }}
        >
          Shift Remarks
        </Button>
      </Tooltip>

      {/* Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        TransitionComponent={SlideTransition}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            maxHeight: "90vh",
            background: subtleGlass,
            backdropFilter: "blur(18px)",
            border: surfaceBorder,
            overflow: "hidden",
          },
        }}
        aria-labelledby="shift-remarks-dialog-title"
      >
        {/* Title */}
        <DialogTitle
          id="shift-remarks-dialog-title"
          sx={{
            p: 0,
            background: headerGradient,
            color: theme.palette.common.white,
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.18)}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isViewingDetails && (
                <IconButton
                  onClick={() => setIsViewingDetails(false)}
                  size="small"
                  aria-label="Go back to add remarks"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.9),
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.common.white, 0.12),
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                {isViewingDetails ? "Shift Details" : "Add Shift Remarks"}
              </Typography>
              {Employee_ID && (
                <Chip
                  label={`ID: ${Employee_ID}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: theme.palette.common.white,
                    borderColor: alpha(theme.palette.common.white, 0.5),
                    backgroundColor: alpha(theme.palette.common.black, 0.06),
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {!isViewingDetails && (
              <Button
                onClick={handleViewShiftDetails}
                variant="outlined"
                startIcon={
                  loadingShiftDetails ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <VisibilityIcon />
                  )
                }
                disabled={loadingShiftDetails}
                size="small"
                sx={{
                  textTransform: "none",
                  borderColor: alpha(theme.palette.common.white, 0.6),
                  color: theme.palette.common.white,
                  "&:hover": {
                    borderColor: theme.palette.common.white,
                    backgroundColor: alpha(theme.palette.common.white, 0.12),
                  },
                }}
              >
                {loadingShiftDetails ? "Loading..." : "View Details"}
              </Button>
            )}

            <IconButton
              onClick={handleCloseDialog}
              size="small"
              aria-label="Close dialog"
              sx={{
                color: alpha(theme.palette.common.white, 0.9),
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.12),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            pt: 3,
            minHeight: 200,
            background: alpha(theme.palette.background.default, 0.6),
          }}
        >
          <Fade in timeout={300}>
            <Box>
              {isViewingDetails ? (
                // Details view
                shiftDetails ? (
                  <TableContainer
                    sx={{
                      borderRadius: 2,
                      border: surfaceBorder,
                      backgroundColor: alpha(theme.palette.background.paper, 0.85),
                      "& .MuiTableCell-root": {
                        borderBottom: surfaceBorder,
                      },
                    }}
                  >
                    <Table>
                      <TableBody>
                        {[
                          {
                            label: "Shift Logged In",
                            value: formatShiftLoggedIn(
                              shiftDetails.shift_loggedIn_DateTime
                            ),
                          },
                          { label: "Machine Code", value: shiftDetails.machineCode },
                          { label: "Operator", value: shiftDetails.operator },
                          {
                            label: "Assistant Operators",
                            value: shiftDetails.AstOperator,
                          },
                          { label: "Helpers", value: shiftDetails.Helpers },
                          {
                            label: "Current Remarks",
                            value: shiftDetails.remarks || "None",
                          },
                        ].map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.05
                                ),
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.text.secondary,
                                width: "30%",
                              }}
                            >
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily:
                                  row.label === "Current Remarks"
                                    ? "monospace"
                                    : "inherit",
                                whiteSpace:
                                  row.label === "Current Remarks"
                                    ? "pre-wrap"
                                    : "normal",
                              }}
                            >
                              {row.value || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box textAlign="center" py={4}>
                    <Typography variant="body1" color="text.secondary">
                      No shift details found for this employee.
                    </Typography>
                  </Box>
                )
              ) : (
                // Remarks input
                <Box>
                  <TextField
                    fullWidth
                    label="Enter your remarks"
                    multiline
                    rows={isMobile ? 3 : 4}
                    value={remarks}
                    onChange={(e) => {
                      if (e.target.value.length <= CHARACTER_LIMIT) {
                        setRemarks(e.target.value);
                      }
                    }}
                    placeholder="Describe any notable events, issues, or observations during the shift..."
                    helperText={getRemarksHelperText()}
                    color={getRemarksColor()}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.9
                        ),
                        "&:hover": {
                          backgroundColor: theme.palette.background.paper,
                        },
                      },
                    }}
                    inputProps={{
                      maxLength: CHARACTER_LIMIT,
                      "aria-describedby": "remarks-helper-text",
                    }}
                  />

                  {remarks.trim() && (
                    <Box
                      mt={2}
                      p={2}
                      sx={{
                        borderRadius: 2,
                        backgroundColor: alpha(
                          theme.palette.text.primary,
                          0.04
                        ),
                        border: `1px dashed ${alpha(
                          theme.palette.divider,
                          0.8
                        )}`,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Preview (with timestamp):
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace">
                        {remarks.trim()} - ({formatDateTime(new Date())})
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Fade>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            borderTop: surfaceBorder,
            backgroundColor: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: "blur(6px)",
            pt: 2,
            px: 3,
            pb: 2,
          }}
        >
          {!isViewingDetails && (
            <Button
              onClick={handleUpdateRemarks}
              disabled={!isRemarksValid() || loadingUpdate}
              variant="contained"
              startIcon={
                loadingUpdate ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
              }
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                color: theme.palette.common.white,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.25)}`,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 12px 30px ${alpha(theme.palette.success.main, 0.35)}`,
                },
              }}
            >
              {loadingUpdate ? "Saving..." : "Save Remarks"}
            </Button>
          )}

          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
            }}
          >
            {isViewingDetails ? "Close" : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.2)}`,
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}
